import { Component, NgModuleRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import lodash from 'lodash';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { HorizonToastService } from '@bayclubs/horizon';
import { ConfirmationService, ModalService, TimeService, utils } from '@bayclubs/utils';

import { GlobalEventsService, GroupClassesSecurityService, OnAddEditGroupClass } from '@infrastructure/infrastructure.module';
import { SecuritySignalService } from '@infrastructure/services/security-signal.service';
import {
    FiltersStorageService,
    GroupClassesApiService,
    ManagementContextResolverService,
    SubBoardApiService
} from '@shared/shared.module';
import { bindPageHeaderAction } from '@shared/components/page-header/page-header.actions';
import { CalendarEventModel, CalendarMode, CalendarModel, CategoryModel, ClubModel, ContextModel, CopyGroupClassCommandOptions, CopyGroupClassesScheduleCommand, CopyGroupClassesScheduleMethod, GetGroupClassesCalendarQuery, InstructorModel, LocationModel } from '@bayclubs/group-classes-employee-api';
import { ClassesCalendarFacade } from '../../+store/classes-calendar.facade';
import { CalendarEventsService } from '../../components/calendar/calendar-events.service';
import { InstructorSubstitutionComponent } from '../../components/instructor-substitution/instructor-substitution.component';
import { RecurringClassActionConfirmationComponent } from '../../components/recurring-class-action-confirmation/recurring-class-action-confirmation.component';
import { RemoveClassService } from '../../services/remove-class.service';

@Component({
    selector: 'app-group-classes-calendar',
    templateUrl: './group-classes-calendar.component.html',
    styleUrls: ['./group-classes-calendar.component.scss'],
    standalone: false
})
export class GroupClassesCalendarComponent implements OnInit, OnDestroy {
    public searchForm: UntypedFormGroup;

    public model: CalendarModel;

    public clubId: string;
    private context: ContextModel;

    private subscriptions: Subscription[] = [];
    private initialQueryParams: ParamMap;

    private readonly addClass = () => {
        this.globalEventsService.onAddEditGroupClass$.emit(new OnAddEditGroupClass());
    }

    constructor(
        private time: TimeService,
        private groupClassesApi: GroupClassesApiService,
        private subBoardApi: SubBoardApiService,
        private formBuilder: UntypedFormBuilder,
        private globalEventsService: GlobalEventsService,
        private calendarEventsService: CalendarEventsService,
        private toast: HorizonToastService,
        private confirmation: ConfirmationService,
        private translate: TranslateService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private filtersStorageService: FiltersStorageService,
        private modal: ModalService,
        private moduleRef: NgModuleRef<any>,
        private security: GroupClassesSecurityService,
        private modalService: ModalService,
        private removeClassService: RemoveClassService,
        private contextResolverService: ManagementContextResolverService,
        private classesCalendarFacade: ClassesCalendarFacade,
        private securitySignals: SecuritySignalService
    ) {
        this.initialQueryParams = activatedRoute.snapshot.queryParamMap;

        bindPageHeaderAction(
            () => this.securitySignals.canManageClasses(),
            'Add class',
            this.addClass
        );
    }

    ngOnInit() {
        this.subscriptions.push(
            this.contextResolverService.refreshContext().subscribe(context => {
                this.context = context;

                this.initForm();

                this.subscriptions.push(
                    this.globalEventsService.onGroupClassAdded$.subscribe(value => {
                        this.refreshCalendarData();
                    })
                );

                this.subscriptions.push(
                    this.globalEventsService.onGroupClassUpdated$.subscribe(value => {
                        this.refreshCalendarData();
                    })
                );

                this.subscriptions.push(
                    this.calendarEventsService.copySchedule$.subscribe(
                        (method: CopyGroupClassesScheduleMethod) => {
                            this.copySchedule(method);
                        }
                    )
                );

                this.subscriptions.push(
                    this.calendarEventsService.copySingle$.subscribe(copyData => {
                        this.copyGroupClass(copyData);
                    })
                );

                this.subscriptions.push(
                    this.calendarEventsService.editEvent$.subscribe((event: CalendarEventModel) => {
                        this.editEvent(event);
                    })
                );

                this.subscriptions.push(
                    this.calendarEventsService.cancelEvent$.subscribe(eventId => {
                        this.cancelEvent(eventId);
                    })
                );

                this.subscriptions.push(
                    this.calendarEventsService.restoreEvent$.subscribe(
                        (eventRestoreData: { id: string; hasAlreadyStarted: boolean }) => {
                            this.restoreEvent(eventRestoreData);
                        }
                    )
                );

                this.subscriptions.push(
                    this.calendarEventsService.removeEvent$.subscribe(data => {
                        this.removeEvent(data);
                    })
                );

                this.subscriptions.push(
                    this.calendarEventsService.substituteInstructor$.subscribe((event: CalendarEventModel) => {
                        this.substitute(event);
                    })
                );

                this.subscriptions.push(
                    this.calendarEventsService.cancelSubstitution$.subscribe(eventId => {
                        this.cancelSubstitution(eventId);
                    })
                );

                this.subscriptions.push(
                    this.calendarEventsService.subRequestEvent$.subscribe((event: CalendarEventModel) => {
                        this.requestSubstitution(event);
                    })
                );
                this.refreshCalendarData();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(i => i.unsubscribe());
    }

    private initForm() {
        const initialCalendarMode: CalendarMode = <CalendarMode>this.initialQueryParams.get('calendarMode') || CalendarMode.Week;

        const now = this.time.nowInTimeZone(this.time.defaultTimezone());
        const queryParamsDate = this.initialQueryParams.get('date');
        const initialDate = queryParamsDate ? this.time.toBcDate(moment(queryParamsDate)) : this.time.toBcDate(now);
        const substitutedOnly = this.initialQueryParams.get('substitutedOnly');

        let clubId = this.initialQueryParams.get('clubId');
        let locationIds = this.initialQueryParams.getAll('locations');
        this.clubId = clubId;
        this.classesCalendarFacade.setSelectedClubId(this.clubId);
        this.security.filterClubsForViewClasses(this.context.clubs).subscribe(clubs => {
            if (!clubId || !clubs.find(x => x.id === clubId)) {
                clubId = this.getDefaultClubId();

                locationIds = [];
                this.onClubIdChanged(clubId);
            }

            this.loadInitialCalendarModel(clubId, initialCalendarMode, initialDate);

            const categoriesCodes: string[] = this.initialQueryParams.getAll('categories');
            const categories: CategoryModel[] = this.context.categories.filter(c =>
                lodash.includes(categoriesCodes, c.code)
            );

            const locations: LocationModel[] = this.context.locations.filter(c =>
                lodash.includes(locationIds, c.id)
            );

            this.searchForm = this.formBuilder.group({
                date: [initialDate, []],
                calendarMode: [initialCalendarMode, []],
                clubId: [clubId, []],
                locationIds: [locations, []],
                categories: [categories, []],
                substitutedOnly: [substitutedOnly, []]
            });

            this.searchForm.valueChanges.pipe(debounceTime(200)).subscribe(x => {
                this.refreshCalendarData();
            });

            this.subscribeToFormControlChangesToUpdateQueryParams();

            this.searchForm.controls['calendarMode'].valueChanges.pipe(debounceTime(500)).subscribe(value => {
                if (value === CalendarMode.Day) {
                    this.searchForm.controls['locationIds'].setValue(null);
                }
            });
        });
    }

    private loadInitialCalendarModel(clubId: string, calendarMode: CalendarMode, date: utils.BcDate) {
        this.model = <CalendarModel>{
            calendarMode: calendarMode,
            currentDate: date,
            events: [],
            locations: this.getLocations(clubId)
        };
    }

    private onClubIdChanged(value: string) {
        const clubParam: Params = { clubId: value, locations: [], categories: [] };
        this.clubId = value;
        this.classesCalendarFacade.setSelectedClubId(this.clubId);
        this.mergeQueryParam(clubParam);

        if(this.model) {
            this.model.locations = this.getLocations(this.clubId);
        }
    }

    private getLocations(clubId): LocationModel[] {
        const otherLocation = <LocationModel>{

            clubId: clubId,
            id: 'other',
            name: 'Other',
            type: 'Regular',
            order: 1000000 // just to always be last
        };

        const locations = [
            ...this.context.locations.filter(p => p.clubId === clubId),
            otherLocation
        ]

        return locations;
    }

    private subscribeToFormControlChangesToUpdateQueryParams() {
        this.searchForm.controls['clubId'].valueChanges.subscribe(value => this.onClubIdChanged(value));

        this.searchForm.controls['calendarMode'].valueChanges.subscribe(value => {
            const calendarModeParam: Params = { calendarMode: value };
            this.mergeQueryParam(calendarModeParam);
        });

        this.searchForm.controls['date'].valueChanges.subscribe(value => {
            const dateParam: Params = { date: value.value };
            this.mergeQueryParam(dateParam);
        });

        this.searchForm.controls['categories'].valueChanges.subscribe((value: CategoryModel[]) => {
            const categoriesCodes = value.map(item => {
                return item.code;
            });
            const categoriesParam: Params = { categories: categoriesCodes };
            this.mergeQueryParam(categoriesParam);
        });

        this.searchForm.controls['locationIds'].valueChanges.subscribe(value => {
            const locationsParam: Params = { locations: value?.map(x=>x.id) };
            this.mergeQueryParam(locationsParam);
        });

        this.searchForm.controls['substitutedOnly'].valueChanges.subscribe(value => {
            const substitutedOnlyParam: Params = { substitutedOnly: value };
            this.mergeQueryParam(substitutedOnlyParam);
        });
    }

    private mergeQueryParam(params: Params): void {
        this.router
            .navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: params,
                queryParamsHandling: 'merge'
            })
            .then(() => (this.filtersStorageService.calendarUrl = this.router.url));
    }

    private getDefaultClubId(): string {
        if (this.context.clubs && this.context.clubs.length > 0) {
            let clubs = [];

            this.security.filterClubsForViewClasses(this.context.clubs).subscribe(filtered => (clubs = filtered));

            if (clubs.length > 0) {
                return clubs[0].id;
            }
        }
    }

    private copySchedule(option: CopyGroupClassesScheduleMethod): void {
        let groupClassIds = this.extractGroupClassesIdsFromModel();

        let request = <CopyGroupClassesScheduleCommand>{
            copyMethod: option,
            groupClassIds: groupClassIds
        };

        this.groupClassesApi.copySchedule(request).subscribe(result => {
            this.showOperationSuccess();
            this.refreshCalendarData();
        });
    }

    private copyGroupClass(copyData: any): void {
        let request = <CopyGroupClassCommandOptions>{
            copyMethod: copyData.method,
            instructorId: copyData.instructorId
        };

        this.groupClassesApi.copySingle(copyData.groupClassId, request).subscribe(result => {
            this.showOperationSuccess();
            this.refreshCalendarData();
        });
    }

    public editEvent(event: CalendarEventModel): void {
        const clubId = this.searchForm.controls.clubId.value;
        this.subBoardApi.getOpenRequest(clubId, event.id).subscribe(response => {
            const hasOpenSubstitutionRequest = response != null;

            if (hasOpenSubstitutionRequest || event.hasActiveBookings || event.hasAlreadyStarted) {
                const bodyText: string = this.translate.instant('GroupClassDetails.EditConfirmationText');

                const headerText: string = this.translate.instant('GroupClassDetails.EditConfirmationTitle');
                // because we don't want to show options if we want to edit this class or this and following
                // before editing class
                const confirmationEvent = { isRecurring: false, changedFields: [] };
                const params = {
                    event: confirmationEvent,
                    warningBar: false,
                    bodyText: bodyText,
                    headerText: headerText,
                    confirmed: (includeFollowing: boolean) => {
                        this.executeEditEvent(event);
                    }
                };

                this.modalService
                    .create<RecurringClassActionConfirmationComponent>(
                        RecurringClassActionConfirmationComponent,
                        params,
                        this.moduleRef,
                        '#confirmationModal'
                    )
                    .subscribe(() => {});
            } else {
                this.executeEditEvent(event);
            }
        });
    }

    private executeEditEvent(event: CalendarEventModel) {
        this.globalEventsService.onAddEditGroupClass$.emit(new OnAddEditGroupClass(event.id));
    }

    private cancelEvent(event: CalendarEventModel): void {
        const bodyText: string = event.isRecurring
            ? this.translate.instant('GroupClassDetails.CancellationRecurringConfirmationText')
            : this.translate.instant('GroupClassDetails.CancellationConfirmationText');

        const headerText: string = event.isRecurring
            ? this.translate.instant('GroupClassDetails.CancellationHeader')
            : this.translate.instant('GroupClassDetails.CancellationConfirmationTitle');

        const params = {
            event,
            warningBar: true,
            bodyText: bodyText,
            headerText: headerText,
            confirmed: (includeFollowing: boolean) => {
                this.groupClassesApi.cancel(event.id, includeFollowing).subscribe(() => {
                    this.showOperationSuccess();
                    this.refreshCalendarData();
                });
            }
        };

        this.modalService
            .create<RecurringClassActionConfirmationComponent>(
                RecurringClassActionConfirmationComponent,
                params,
                this.moduleRef,
                '#confirmationModal'
            )
            .subscribe(() => {});
    }

    private restoreEvent(eventRestoreData: { id: string; hasAlreadyStarted: boolean }): void {
        const messageKey =
            'GroupClassDetails.' +
            (eventRestoreData.hasAlreadyStarted ? 'RestorePastClassConfirmationText' : 'RestoreRegularConfirmationText');

        this.confirmation
            .show(
                this.translate.instant('GroupClassDetails.RestoreConfirmationTitle'),
                this.translate.instant(messageKey),
                this.translate.instant('General.Confirm'),
                this.translate.instant('General.Dismiss')
            )
            .subscribe(decision => {
                if (decision) {
                    this.groupClassesApi.restore(eventRestoreData.id).subscribe(() => {
                        this.showOperationSuccess();
                        this.refreshCalendarData();
                    });
                }
            });
    }

    private removeEvent(event: CalendarEventModel): void {
        const clubId = this.searchForm.controls.clubId.value;

        this.removeClassService.removeClass(event, () => this.refreshCalendarData(), clubId);
    }

    public substitute(event: CalendarEventModel): void {
        const clubId = this.searchForm.controls.clubId.value;

        this.subBoardApi.getOpenRequest(clubId, event.id).subscribe(response => {
            const hasOpenSubstitutionRequest = response != null;

            if (hasOpenSubstitutionRequest) {
                this.confirmation
                    .show(
                        this.translate.instant('GroupClassDetails.SubstitutionRequestExistsSubstituteConfirmationTitle'),
                        this.translate.instant('GroupClassDetails.SubstitutionRequestExistsSubstituteConfirmationText'),
                        this.translate.instant('General.Yes'),
                        this.translate.instant('General.No')
                    )
                    .subscribe(result => {
                        if (result == true) {
                            this.executeSubstitute(event);
                        }
                    });
            } else {
                this.executeSubstitute(event);
            }
        });
    }

    private executeSubstitute(event: CalendarEventModel): void {
        let params = {
            instructor: event.instructor,
            club: <ClubModel>{ id: this.searchForm.controls.clubId.value }
        };

        this.modal
            .create<InstructorSubstitutionComponent>(InstructorSubstitutionComponent, params, this.moduleRef)
            .subscribe(result => {
                if (result && result.selectedSubstituteInstructor) {
                    this.confirmSubstitution(event, result.selectedSubstituteInstructor);
                }
            });
    }

    public confirmSubstitution(
        event: CalendarEventModel,
        selectedSubstituteInstructor: InstructorModel
    ) {
        this.groupClassesApi.substitute(event.id, selectedSubstituteInstructor.id).subscribe(() => {
            this.showOperationSuccess();
            this.refreshCalendarData();
        });
    }

    public cancelSubstitution(eventId): void {
        this.confirmation
            .show(
                this.translate.instant('GroupClassDetails.CancellationConfirmationTitle'),
                this.translate.instant('GroupClassDetails.SubstitutionCancellationConfirmationText'),
                this.translate.instant('General.Confirm'),
                this.translate.instant('General.Dismiss')
            )
            .subscribe(result => {
                if (result) {
                    this.groupClassesApi.cancelSubstitution(eventId).subscribe(() => {
                        this.showOperationSuccess();
                        this.refreshCalendarData();
                    });
                }
            });
    }

    public requestSubstitution(event: CalendarEventModel): void {
        const clubId = this.searchForm.controls.clubId.value;
        this.subBoardApi.requestSubstitution(clubId, event.id).subscribe(() => {
            this.showOperationSuccess();
            this.refreshCalendarData();
        });
    }

    private refreshCalendarData() {
        let query = this.createCalendarQuery();
        this.classesCalendarFacade.loadClassesCalendar(query);
    }

    private showOperationSuccess() {
        this.toast.success(this.translate.instant('Utils.OperationOk'));
    }

    private createCalendarQuery() {
        const locationIdsValue: LocationModel[] = this.searchForm.controls['locationIds'].value || [];
        const showClassesWithoutLocation: boolean = locationIdsValue.map(x => x.id).some(x => x === 'other');
        const query = {
            date: this.searchForm.controls['date'].value,
            calendarMode: this.searchForm.controls['calendarMode'].value,
            clubId: this.searchForm.controls['clubId'].value,
            locationIds: locationIdsValue.map(x => x.id).map(x => (x == 'other' ? null : x)),
            categoryCodes: this.searchForm.controls['categories'].value.map(c => c.code),
            showOnlySubstitutedClasses: this.searchForm.controls['substitutedOnly'].value,
            showClassesWithoutLocation: showClassesWithoutLocation
        } as GetGroupClassesCalendarQuery;
        return query;
    }

    private extractGroupClassesIdsFromModel(): Array<string> {
        return this.classesCalendarFacade.eventIdsSnapshot();
    }
}
